import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { CK_CONFIGS } from "configs/ckEditor";
import validate from "validate.js";
import { isEmpty, forEach } from "lodash";
import { timeZonesListFetch, hideTimeZonesValidationError, addTimeZones, updateTimeZones} from "actions";

const schema = {
  name: {
    presence: { allowEmpty: false, message: "^Timezone name is required" },
  },
  offset: {
    presence: { allowEmpty: false, message: "^Timezone offset is required" },
  },
};

const AddUpdateModal = (props) => {
  const dispatch = useDispatch();
  const timeZonesState = useSelector((state) => state.timeZonesState);

  const [formState, setFormState] = useState({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  useEffect(() => {
    if (timeZonesState.showUpdateForm) {
      const record = timeZonesState.timeZonesRecord;

      setFormState((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          name: record.name || "",
          id: record.id || "",
          offset: record.offset || "",
          description: record.description || "",
        },
        touched: {
          ...prevState.touched,
          name: true,
          id: true,
          offset: true,
          description: true,
        },
      }));
    }
  }, [timeZonesState.timeZonesRecord]);

  useEffect(() => {
    if (!isEmpty(timeZonesState.validation_error)) {
      const errors = timeZonesState.validation_error;
      setFormState((formState) => ({
        ...formState,
        isValid: errors ? false : true,
        errors: errors || {},
      }));
    }
  }, [timeZonesState.validation_error]);

  useEffect(() => {
    if (timeZonesState.redirect_to_list) {
      setFormState({
        isValid: false,
        values: {},
        touched: {},
        errors: {},
      });

      props.onHide();
      dispatch(timeZonesListFetch());
    }
  }, [timeZonesState.redirect_to_list]);

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState((formState) => ({
      ...formState,
      isValid: errors ? false : true,
      errors: errors || {},
    }));
  }, [formState.values]);

  const handleChange = (event) => {
    event.persist();
    setFormState((formState) => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]:
          event.target.type === "checkbox"
            ? event.target.checked
            : event.target.value,
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true,
      },
    }));
    dispatch(hideTimeZonesValidationError(event.target.name));
  };

  const setDescription = (description) => {
    setFormState((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        description: description,
      },
      touched: {
        ...prevState.touched,
        description: true,
      },
    }));
    dispatch(hideTimeZonesValidationError("description"));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const data = new FormData();

    forEach(formState.values, (value, key) => {
      data.append(key, value);
    });

    if (timeZonesState.showUpdateForm) {
      await dispatch(updateTimeZones(data));
    } else {
      await dispatch(addTimeZones(data));
    }
  };

  const hasError = (field) =>
    formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <div className="p-4">
        <Modal.Header className="p-0 border-0 justify-content-center mb-3 border-bottom">
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="font-24 font-weight-s-bold"
          >
            {timeZonesState.showUpdateForm
              ? "Update Timezone"
              : "Add New Timezone"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 border-0">
          <Row>
            <Col lg={6}>
              <Form.Group className="mb-3" controlId="formBasicName">
                <Form.Label className="font-13 font-weight-medium">
                  Enter your timezone name{" "}
                  <span className="form-star-span">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Timezone name"
                  value={formState.values.name || ""}
                  name="name"
                  onChange={handleChange}
                  className="h-60 font-13 font-weight-normal ps-4 input-clr border-radius-14"
                  isInvalid={hasError("name")}
                />
                <Form.Control.Feedback type="invalid">
                  {hasError("name") ? formState.errors.name[0] : null}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="mb-3" controlId="formBasicOffset">
                <Form.Label className="font-13 font-weight-medium">
                  Enter Offset <span className="form-star-span">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Timezone Offset"
                  value={formState.values.offset || ""}
                  onChange={handleChange}
                  name="offset"
                  className="h-60 font-13 font-weight-normal ps-4 input-clr border-radius-14"
                  isInvalid={hasError("offset")}
                />
                <Form.Control.Feedback type="invalid">
                  {hasError("offset") ? formState.errors.offset[0] : null}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <div className="my-3">
            <CKEditor
              editor={ClassicEditor}
              config={CK_CONFIGS(localStorage.getItem("token"))}
              data={formState.values.description || ""}
              onChange={(event, editor) => {
                const data = editor.getData();
                setDescription(data);
              }}
            />
            {hasError("description") && (
              <Form.Text className="text-danger">
                {formState.errors.description[0]}
              </Form.Text>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <Button className="application-cancel-btn" onClick={props.onHide}>
            Cancel
          </Button>
          <Button
            className="font-13 font-weight-normal application-save-btn"
            onClick={handleSubmit}
            disabled={!formState.isValid}
          >
            {timeZonesState.showUpdateForm ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default AddUpdateModal;
